import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ieltsApi, type WritingTask, type WritingTaskInput, type WritingTaskType } from '@/api/ielts';
import { humanizeError } from '@/lib/errors';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MAdd, MBlock, MEmpty, MRow, MTitle } from '@/components/mobile/MobileKit';
import { MoreButton } from '@/components/mobile/MoreButton';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * IELTS Writing tasks — admin/manager content management (Feature #3a).
 *
 * Mirrors AdminStoriesPage / AdminMaterialsPage: list of cards + a create/edit
 * modal. Reachable by both `admin` and `manager` (content is not financial);
 * the server enforces RequireRole("admin","manager").
 */
export function AdminIeltsWritingPage() {
  const ask = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [editing, setEditing] = useState<WritingTask | null>(null);
  const [sheetFor, setSheetFor] = useState<WritingTask | null>(null);
  const isMobile = useIsMobile();

  const list = useQuery({
    queryKey: ['admin', 'ielts', 'writing-tasks'],
    queryFn: ieltsApi.adminListTasks,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'ielts', 'writing-tasks'] });
  const remove = useMutation({
    mutationFn: (id: string) => ieltsApi.adminDeleteTask(id),
    onSuccess: invalidate,
  });

  const tasks = list.data ?? [];
  const askRemove = async (task: WritingTask) => {
    if (await ask({ message: t('ieltsAdmin.deleteConfirm', { title: task.title }), danger: true })) {
      remove.mutate(task.id);
    }
  };
  const modals = (
    <>
      {creatingOpen && <TaskModal onClose={() => setCreatingOpen(false)} onSaved={invalidate} />}
      {editing && <TaskModal task={editing} onClose={() => setEditing(null)} onSaved={invalidate} />}
    </>
  );

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §5.14) ─────────────────────────
  // Карточки по три в ряд → строки «название · Task 1/2 · активно · Band N»; два значка без подписей → «⋯» с листом.
  if (isMobile) {
    return (
      <div className="space-y-3">
        <MTitle
          title={t('ieltsAdmin.title')}
          subtitle={t('ieltsAdmin.subtitle')}
          right={<MAdd label={t('ieltsAdmin.createBtn')} onClick={() => setCreatingOpen(true)} />}
        />
        {remove.error && <p className="px-0.5 text-[13px] text-error">{humanizeError(remove.error)}</p>}
        {list.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : list.error ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(list.error)}</p>
        ) : !tasks.length ? (
          <MEmpty icon="edit_note" title={t('ieltsAdmin.noTasks')} />
        ) : (
          <MBlock>
            {tasks.map((task, i) => (
              <MRow
                key={task.id}
                wrap
                last={i === tasks.length - 1}
                onClick={() => setEditing(task)}
                title={task.title}
                meta={[
                  t(`ieltsAdmin.taskType_${task.task_type}`),
                  task.is_active ? t('ieltsAdmin.statusActive') : t('ieltsAdmin.statusHidden'),
                  task.target_band != null ? t('ieltsAdmin.bandShort', { band: task.target_band }) : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
                aside={<MoreButton onClick={() => setSheetFor(task)} />}
              />
            ))}
          </MBlock>
        )}
        <ActionSheet
          open={!!sheetFor}
          onClose={() => setSheetFor(null)}
          title={sheetFor?.title}
          actions={
            sheetFor
              ? [
                  { label: t('common.edit'), icon: 'edit', onSelect: () => setEditing(sheetFor) },
                  { label: t('common.delete'), icon: 'delete', danger: true, onSelect: () => void askRemove(sheetFor) },
                ]
              : []
          }
        />
        {modals}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={t('ieltsAdmin.title')}
        subtitle={t('ieltsAdmin.subtitle')}
        actions={
          <Button onClick={() => setCreatingOpen(true)}>
            <span className="material-symbols-outlined">add</span>
            {t('ieltsAdmin.createBtn')}
          </Button>
        }
      />

      {remove.error && (
        <Card variant="subtle" className="mb-4 text-sm text-error">{humanizeError(remove.error)}</Card>
      )}

      {list.isLoading ? (
        <Card variant="subtle">{t('common.loading')}</Card>
      ) : list.error ? (
        <Card variant="subtle" className="text-error">{humanizeError(list.error)}</Card>
      ) : !tasks.length ? (
        <Card variant="subtle" className="text-center text-on-surface-variant">
          {t('ieltsAdmin.noTasks')}{' '}
          <button type="button" onClick={() => setCreatingOpen(true)} className="font-semibold text-primary">
            {t('ieltsAdmin.createFirst')}
          </button>
        </Card>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {tasks.map((task) => (
            <li key={task.id}>
              <Card className="flex h-full flex-col">
                <div className="flex items-center gap-2">
                  <Pill variant="primary">{t(`ieltsAdmin.taskType_${task.task_type}`)}</Pill>
                  <Pill variant={task.is_active ? 'success' : 'neutral'}>
                    {task.is_active ? t('ieltsAdmin.statusActive') : t('ieltsAdmin.statusHidden')}
                  </Pill>
                  {task.target_band != null && (
                    <Pill variant="neutral">{t('ieltsAdmin.bandShort', { band: task.target_band })}</Pill>
                  )}
                </div>
                <CardTitle className="mt-3">{task.title}</CardTitle>
                <p className="mt-1 line-clamp-3 text-sm text-on-surface-variant">{task.prompt}</p>
                {task.image_url && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-on-surface-variant">
                    <span className="material-symbols-outlined text-sm">image</span>
                    {t('ieltsAdmin.hasImage')}
                  </p>
                )}
                <div className="mt-auto flex gap-2 pt-3">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(task)}>
                    <span className="material-symbols-outlined">edit</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void askRemove(task)}
                  >
                    <span className="material-symbols-outlined">delete</span>
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {modals}
    </>
  );
}

function TaskModal({ task, onClose, onSaved }: { task?: WritingTask; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const isEdit = Boolean(task);
  const [data, setData] = useState<WritingTaskInput>(
    task
      ? {
          task_type: task.task_type,
          title: task.title,
          prompt: task.prompt,
          image_url: task.image_url ?? '',
          target_band: task.target_band ?? undefined,
          is_active: task.is_active,
        }
      : { task_type: 'task2', title: '', prompt: '', image_url: '', target_band: undefined, is_active: true },
  );
  // Keep target_band editable as a string so the field can be cleared.
  const [bandText, setBandText] = useState(task?.target_band != null ? String(task.target_band) : '');

  const save = useMutation({
    mutationFn: () => {
      const band = bandText.trim() === '' ? undefined : Number(bandText);
      const payload: WritingTaskInput = {
        task_type: data.task_type,
        title: data.title?.trim(),
        prompt: data.prompt?.trim(),
        image_url: data.task_type === 'task1' ? (data.image_url?.trim() || undefined) : undefined,
        target_band: band != null && Number.isFinite(band) ? band : undefined,
        is_active: data.is_active,
      };
      return isEdit ? ieltsApi.adminUpdateTask(task!.id, payload) : ieltsApi.adminCreateTask(payload);
    },
    onSuccess: () => { onSaved(); onClose(); },
  });

  const canSave = !!data.title?.trim() && !!data.prompt?.trim();
  const isMobile = useIsMobile();
  // На телефоне — внизу окна во весь экран (правило 3), на компьютере — под полями, как было.
  const buttons = (
    <div className={isMobile ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2 pt-2'}>
      <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
      <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!canSave}>
        {isEdit ? t('common.save') : t('ieltsAdmin.createBtn')}
      </Button>
    </div>
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? t('ieltsAdmin.editTask') : t('ieltsAdmin.newTask')}
      phoneFull
      footer={isMobile ? buttons : undefined}
    >
      <div className="space-y-3">
        <Select
          label={t('ieltsAdmin.fieldTaskType')}
          value={data.task_type}
          onChange={(e) => setData({ ...data, task_type: e.target.value as WritingTaskType })}
          options={[
            { value: 'task1', label: t('ieltsAdmin.taskType_task1') },
            { value: 'task2', label: t('ieltsAdmin.taskType_task2') },
          ]}
        />
        <Input
          label={t('ieltsAdmin.fieldTitle')}
          value={data.title ?? ''}
          onChange={(e) => setData({ ...data, title: e.target.value })}
        />
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-label text-on-surface-variant">
            {t('ieltsAdmin.fieldPrompt')}
          </span>
          <textarea
            className="min-h-[160px] w-full rounded-button bg-surface-container-lowest p-4 text-on-surface shadow-soft transition focus:shadow-ambient focus:outline-none"
            placeholder={t('ieltsAdmin.fieldPromptHint')}
            value={data.prompt ?? ''}
            onChange={(e) => setData({ ...data, prompt: e.target.value })}
          />
        </label>
        {data.task_type === 'task1' && (
          <Input
            label={t('ieltsAdmin.fieldImageUrl')}
            hint={t('ieltsAdmin.fieldImageUrlHint')}
            value={data.image_url ?? ''}
            onChange={(e) => setData({ ...data, image_url: e.target.value })}
          />
        )}
        <Input
          type="number"
          inputMode="decimal"
          step="0.5"
          min="0"
          max="9"
          label={t('ieltsAdmin.fieldTargetBand')}
          hint={t('ieltsAdmin.fieldTargetBandHint')}
          value={bandText}
          onChange={(e) => setBandText(e.target.value)}
        />
        <Select
          label={t('ieltsAdmin.fieldStatus')}
          value={data.is_active ? 'on' : 'off'}
          onChange={(e) => setData({ ...data, is_active: e.target.value === 'on' })}
          options={[
            { value: 'on', label: t('ieltsAdmin.statusActive') },
            { value: 'off', label: t('ieltsAdmin.statusHidden') },
          ]}
        />
        {save.error && <p className="text-sm text-error">{humanizeError(save.error)}</p>}
        {!canSave && <p className="text-xs text-on-surface-variant">{t('ieltsAdmin.validationHint')}</p>}

        {!isMobile && buttons}
      </div>
    </Modal>
  );
}
